import { MetricUnit } from '@/__generated__/graphql';
import { useFormatters } from '@/hooks/useFormatters';
import { AU_TINT } from '@/theme/au-compat';
import { BatteryLevelIcon } from '@components/BatteryLevelIcon';
import { BATTERY_LOW_THRESHOLD } from '@features/gateCommon/utils/battery';
import { msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { BatteryUnknown } from '@mui/icons-material';
import { Box, CircularProgress } from '@mui/material';
import { useQuery } from '@apollo/client';
import { type FC } from 'react';
import { GET_GATE_LAST_BATTERY } from '../graphql/queries/getGateLastBattery';
import type { GateLiveStatusState } from '../hooks/useGateLiveStatus';

/**
 * Batería del equipo en el título, con la misma forma que la del panel de operación. Sin lectura en
 * vivo cae a la registrada, sin antigüedad, como la tira del panel: las dos superficies muestran
 * el mismo valor para la misma compuerta.
 */
export const LiveBattery: FC<{ gateId: string | null; live: GateLiveStatusState }> = ({
  gateId,
  live,
}) => {
  const { _ } = useLingui();
  const { formatMetricValue } = useFormatters();
  // `useQuery` y no una suspense query: el título queda fuera del `Suspense` del cuerpo.
  const { data } = useQuery(GET_GATE_LAST_BATTERY, {
    variables: { gateId: gateId ?? '' },
    skip: !gateId,
    fetchPolicy: 'network-only',
  });
  if (!gateId) return null;

  const registered =
    data?.gate.id === gateId ? data.gate.movements2.lastBattery?.value ?? null : null;
  const battery = live.reading?.batteryLevel ?? registered;
  // `!== null` y no falsy: una batería en 0 es un dato, y de los graves.
  const low = battery !== null && battery < BATTERY_LOW_THRESHOLD;

  return (
    <Box
      aria-label={_(msg`Batería`)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: 11.5,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        flexShrink: 0,
      }}
    >
      {live.awaiting ? (
        <CircularProgress size={12} thickness={5} color="inherit" />
      ) : battery !== null ? (
        <>
          <BatteryLevelIcon
            battery={battery}
            sx={{ fontSize: 16, color: low ? AU_TINT.error.text : undefined }}
          />
          {formatMetricValue(battery, { unit: MetricUnit.Percent, precision: 0 })}
        </>
      ) : (
        <>
          <BatteryUnknown sx={{ fontSize: 16 }} />
          N/D
        </>
      )}
    </Box>
  );
};
